import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FiFileUpload2Service } from '@fi-shared/fi-insert-media/fi-file-upload2.service';
import { IInsertMediaUploadItem } from '@fi-shared/fi-insert-media/fi-insert-media';
import { Subject } from 'rxjs';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiInsertMediaUploadHelperComponent } from '@fi-shared/fi-insert-media/fi-insert-media-upload-helper/fi-insert-media-upload-helper.component';
import { UserPreferencesService } from '@fi-shared/fi-shared-services/user-preferences.service';
import { FiDisplayType } from '@fi-shared/types/display-type';
import { isDefined } from '@fi-shared/utils';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { FileType } from '@fi-shared/types';
import { getAcceptByFileType } from '@fi-shared/utils/get-accept-by-file-type';
import { FiDndDetails, FiDndEventType } from '@fi-shared/fi-insert-media/fi-dnd-overlay/fi-dnd-helper';

@Component({
    selector: 'fi-insert-media',
    template: `
        <fi-dnd-overlay
            [parentElementRef]="wrapperElementRef"
            [fileTypes]="allowedFileTypes"
            (uploadFiles)="onFilesSelect($event)"
            (dndDetails)="onDndDetails($event)"
        >
        </fi-dnd-overlay>
        <div class="fi-insert-media-container">
            <fi-insert-media-details
                [uploadStarted$]="uploadStarted$.asObservable()"
                [uploadCancelled$]="uploadCancelled$.asObservable()"
                [uploadFinished$]="uploadFinished$.asObservable()"
                [allowStoreInMyFiles]="allowStoreInMyFiles"
                [allowedFileTypes]="allowedFileTypes"
                [multiple]="multiple"
                [isTableDisplayType]="isTableDisplayType()"
                (selectedUploadItemsChange)="onSelectedUploadItemsChanged($event)"
                (onInsertMediaDisplayTypeChange)="setInsertMediaDisplayType($event)"
            >
                <div class="fi-insert-media-details-top-content">
                    <fi-insert-media-upload-helper
                        #uploadHelper
                        [accept]="accept"
                        [multiple]="multiple"
                        (onFilesSelect)="onFilesSelect($event)"
                    >
                    </fi-insert-media-upload-helper>

                    <span class="heading bold">{{ 'common.attachments' | fiAppText }}</span>
                </div>
            </fi-insert-media-details>
        </div>
    `,
    styleUrls: ['./fi-insert-media.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-insert-media' },
    standalone: false,
})
export class FiInsertMediaComponent implements AfterViewInit, OnInit {
    @Input() attachments: IInsertMediaUploadItem[];
    @Input() wrapperElementRef: ElementRef;
    @Output() attachmentsChange = new EventEmitter<IInsertMediaUploadItem[]>();
    @Input() accept: string;
    @Input() multiple: boolean = true;
    @Input() filesToUpload: ArrayLike<File>;
    @Input() allowStoreInMyFiles: boolean;
    @Input() allowedFileTypes: FileType[];

    // resolve promise with 'false' for cancel selecting item
    @Input() onFilesSelecting: (files: ArrayLike<File>) => Promise<boolean>;
    @Output() showInsertFileErrorTooltip = new EventEmitter<void>();

    @ViewChild('uploadHelper') uploadHelper: FiInsertMediaUploadHelperComponent;

    uploadStarted$ = new Subject<IInsertMediaUploadItem>();
    uploadCancelled$ = new Subject<IInsertMediaUploadItem>();
    uploadFinished$ = new Subject<IInsertMediaUploadItem>();

    insertMediaListDisplayTypeEnum = FiDisplayType;
    currentMediaListDisplayType: number;
    private allowedMimeTypes: string;

    constructor(
        private fileUpload2Service: FiFileUpload2Service,
        private elementRef: ElementRef<HTMLElement>,
        private detectDeviceService: DetectDevicesService,
        private userPreferencesService: UserPreferencesService,
    ) {}

    ngOnInit() {
        if (!this.onFilesSelecting) {
            this.onFilesSelecting = () => Promise.resolve<boolean>(true);
        }
        this.getCurrentMediaListDisplayType();
        this.setDefaultWrapperElementRef();
        if (this.allowedFileTypes) {
            this.allowedMimeTypes = getAcceptByFileType(this.allowedFileTypes, undefined);
        }
    }

    private setDefaultWrapperElementRef() {
        if (!isDefined(this.wrapperElementRef)) this.wrapperElementRef = this.elementRef;
    }

    getCurrentMediaListDisplayType() {
        this.setInsertMediaDisplayType(this.insertMediaListDisplayTypeEnum.Grid);

        /*
        this.currentMediaListDisplayType = this.userPreferencesService.getListDisplayType('FiInsertMedia');
        if (typeof this.currentMediaListDisplayType !== 'number') {
            this.setInsertMediaDisplayType(this.insertMediaListDisplayTypeEnum.List);
        }
*/
    }

    setInsertMediaDisplayType(type: number) {
        this.currentMediaListDisplayType = type;
        this.userPreferencesService.saveListDisplayType(FiItemsListType.InsertMedia, type);
    }

    isTableDisplayType(): boolean {
        return this.currentMediaListDisplayType === this.insertMediaListDisplayTypeEnum.List;
    }

    ngAfterViewInit(): void {
        if (this.filesToUpload) {
            this.onFilesSelect(this.filesToUpload);
        }
    }

    onBrowseDeviceInvoked() {
        this.uploadHelper.onBrowseDeviceInvoked();
    }

    onUseCameraInvoked() {
        this.uploadHelper.onUseCameraInvoked();
    }

    isMobilePhone() {
        return this.detectDeviceService.isMobilePhone();
    }

    onFilesSelect($event: ArrayLike<File>) {
        const unsupportedFiles = Array.from($event).filter(
            (file) => !this.fileUpload2Service.isUploadAllowed(file.type, this.allowedMimeTypes),
        );
        if (!unsupportedFiles.length) {
            this.onFilesSelecting($event).then((result) => {
                if (result) {
                    this.fileUpload2Service.uploadFiles({
                        files: $event,
                        onUploadStarted: (uploadItem) => this.onUploadStarted(uploadItem),
                        onUploadCancelled: (uploadItem) => this.onUploadCancelled(uploadItem),
                        onUploadFinished: (uploadItem) => this.onUploadFinished(uploadItem),
                    });
                }
            });
        } else {
            this.showInsertFileErrorTooltip.emit();
        }
    }

    onSelectedUploadItemsChanged($event: IInsertMediaUploadItem[]) {
        this.attachments = $event;
        this.attachmentsChange.emit(this.attachments);
    }

    protected onDndDetails($event: FiDndDetails) {
        if ($event.dndEventType === FiDndEventType.DragEnter && !$event.isDndAllowed) {
            this.showInsertFileErrorTooltip.emit();
        }
    }

    private onUploadStarted(uploadItem: IInsertMediaUploadItem) {
        this.uploadStarted$.next(uploadItem);
    }

    private onUploadCancelled(uploadItem: IInsertMediaUploadItem) {
        this.uploadCancelled$.next(uploadItem);
    }

    private onUploadFinished(uploadItem: IInsertMediaUploadItem) {
        this.uploadFinished$.next(uploadItem);
    }
}
